import gql from "tagged-template-noop";

/**
 * GraphQL fragment for billing address fields.
 * This fragment defines the structure of billing address-related data.
 *
 * @typedef {Object} BillingAddressFields
 * @property {string} address1 - The first line of the billing address.
 * @property {string} address2 - The second line of the billing address.
 * @property {string} city - The city of the billing address.
 * @property {string} state - The state of the billing address.
 * @property {string} postalCode - The postal code of the billing address.
 * @property {string} country - The country of the billing address.
 * @property {string} nickName - The nickname for the billing address.
 * @property {string} phone - The phone number associated with the billing address.
 * @property {boolean} isPrimary - Indicates if the billing address is the primary address.
 * @property {boolean} lululemonVerified - Indicates if the address is verified by Lululemon.
 * @property {string} encryptedAddressId - The encrypted identifier for the billing address.
 */
export const billingAddressFieldsFragment = gql`
  fragment billingAddressFields on CreditCard {
    billingAddress {
      id
      firstName
      lastName
      address1
      address2
      city
      state
      postalCode
      country
      phone
    }
  }
`;

/**
 * GraphQL fragment for credit card fields.
 * This fragment defines the structure of credit card-related data in a checkout process.
 *
 * @typedef {Object} CreditCardFields
 * @property {string} id - The unique identifier for the credit card.
 * @property {string} cardType - The type of the credit card (e.g., "Visa", "MasterCard").
 * @property {string} lastFour - The last four digits of the credit card number.
 * @property {boolean} isPrimary - Indicates if the credit card is the primary payment method.
 * @property {string} encryptedCardId - The encrypted identifier for the credit card.
 * @property {string} expiryDate - The expiry date of the credit card in MM/YY format.
 * @property {string} cardholderName - The name of the cardholder.
 */
export const creditCardFieldsFragment = gql`
  fragment creditCardFields on CreditCard {
    id
    cardNumber
    cardHolderName
    cardDescription
    isPrimary
    expirationMonth
    expirationYear
    cardType
    isVerified
    isExpired
  }
`;

export const paymentGroupsFieldsFragment = gql`
  fragment paymentGroupsFields on Bag {
    paymentGroups {
      creditCard {
        id
        paymentMethod
        billingAddress {
          firstName
          lastName
          address1
          address2
          city
          state
          postalCode
          country
          phone
        }
        creditCardInfo {
          cardNumber
          cardType
          cardHolderName
          expirationMonth
          expirationYear
          profileCardId
        }
      }
      giftCard {
        id
        amount
        giftCardInfo {
          cardNumber
          amountAvailable
          giftCardId
        }
      }
      payPal {
        id
        paymentMethod
        amount
        amountAuthorized
        amountDebited
        amountCredited
        billingAddress {
          firstName
          lastName
          address1
          address2
          city
          state
          postalCode
          country
          phone
          email
        }
      }
      afterPay {
        id
        walletAuthAmount
      }
      klarna {
        id
        walletAuthAmount
      }
      applePay {
        id
        paymentMethod
      }
    }
  }
`;

/**
 * GraphQL fragment for summary fields.
 * This fragment defines the structure of summary-related data in a checkout process.
 *
 * @typedef {Object} SummaryFields
 * @property {number} subtotal - The subtotal amount of the checkout.
 * @property {number} tax - The tax amount of the checkout.
 * @property {number} total - The total amount of the checkout.
 * @property {number} shipping - The shipping cost of the checkout.
 * @property {number} discount - The discount applied to the checkout.
 * @property {number} nonGCAmount - The non-gift-card amount in the checkout.
 * @property {number} merchandiseTotalBeforeDiscount - The total merchandise cost before applying discounts.
 * @property {number} gstOrHst - The GST or HST tax amount (specific to regions in Canada).
 * @property {number} qst - The QST tax amount (specific to Quebec, Canada).
 * @property {number} pst - The PST tax amount (specific to certain provinces in Canada).
 * @property {number} amountCoveredByGiftCards - The total amount covered by gift cards.
 * @property {number} rdfSubtotal - The subtotal amount for RDF (Return Discount Fee).
 * @property {string} rdfDesc - The description of the RDF (Return Discount Fee).
 */
export const summaryFieldsFragment = gql`
  fragment summaryFields on Bag {
    summary {
      tax
      total
      shipping
      discount
      subtotal
      gstOrHst
      qst
      pst
      amountCoveredByGiftCards
      nonGCAmount
      merchandiseTotalBeforeDiscount
      rdfSubtotal
      rdfDesc
    }
  }
`;

/**
 * GraphQL fragment for shipping address fields.
 * This fragment defines the structure of shipping address-related data in a checkout process.
 *
 * @typedef {Object} ShippingAddressFields
 * @property {string} address1 - The first line of the shipping address.
 * @property {string} address2 - The second line of the shipping address.
 * @property {string} city - The city of the shipping address.
 * @property {string} state - The state of the shipping address.
 * @property {string} postalCode - The postal code of the shipping address.
 * @property {string} country - The country of the shipping address.
 * @property {string} nickName - The nickname for the shipping address.
 * @property {boolean} isPrimary - Indicates if the shipping address is the primary address.
 */
export const shippingAddressFieldsFragment = gql`
  fragment shippingAddressFields on BagShipping {
    shippingAddress {
      firstName
      lastName
      id
      address1
      address2
      city
      state
      postalCode
      country
      phone
      email
      hashedEmail
    }
  }
`;

/**
 * GraphQL fragment for DAV (Delivery Address Verification) address fields.
 * This fragment defines the structure of DAV address-related data in a checkout process.
 *
 * @typedef {Object} DavAddressFields
 * @property {string} address1 - The first line of the DAV address.
 * @property {string} address2 - The second line of the DAV address.
 * @property {string} city - The city of the DAV address.
 * @property {string} state - The state of the DAV address.
 * @property {string} postalCode - The postal code of the DAV address.
 * @property {string} country - The country of the DAV address.
 * @property {boolean} isVerified - Indicates if the DAV address is verified.
 */
export const davAddressFieldsFragment = gql`
  fragment davAddressFields on DavAddress {
    address1
    address2
    city
    state
    postalCode
    country
    countryCode
  }
`;

/**
 * GraphQL fragment for shipping method fields.
 * This fragment defines the structure of shipping method-related data in a checkout process.
 *
 * @typedef {Object} ShippingMethodFields
 * @property {string} code - The code for the shipping method.
 * @property {boolean} isSelected - Indicates if the shipping method is selected.
 * @property {string} displayName - The display name of the shipping method.
 * @property {number} price - The price of the shipping method.
 * @property {string} estimatedDeliveryText - The estimated delivery text for the shipping method.
 * @property {Object} orderBy - The order-by details for the shipping method.
 * @property {string} orderBy.utc - The UTC order-by time.
 * @property {Object} getBy - The get-by details for the shipping method.
 * @property {string} getBy.pacific - The Pacific time for get-by.
 * @property {string} getBy.utc - The UTC time for get-by.
 * @property {string} getBy.eddDisplayString - The estimated delivery date display string.
 */
export const shippingMethodFieldsFragment = gql`
  fragment shippingMethodFields on BagShipping {
    methods {
      code
      isSelected
      displayName
      ribbonTitle
      price
      estimatedDeliveryText
      orderBy {
        utc
      }
      getBy {
        pacific
        utc
        eddDisplayString
      }
    }
    selectedMethod {
      code
      isSelected
      displayName
      ribbonTitle
      price
      estimatedDeliveryText
    }
  }
`;

/**
 * GraphQL fragment for gift message fields.
 * This fragment defines the structure of gift message-related data in a checkout process.
 *
 * @typedef {Object} GiftMessageFields
 * @property {string} id - The unique identifier for the gift message.
 * @property {string} message - The content of the gift message.
 * @property {string} senderName - The name of the sender of the gift message.
 * @property {string} recipientName - The name of the recipient of the gift message.
 */
export const giftMessageFieldsFragment = gql`
  fragment giftMessageFields on Bag {
    giftMessage {
      to
      from
      message
    }
  }
`;

export const bopisStoreFieldsFragment = gql`
  fragment bopisStoreFields on Bag {
    bopisStore {
      id
      name
      phone
      address {
        address1
        address2
        city
        state
        postalCode
        country
      }
      hours {
        monday {
          open
          close
        }
        tuesday {
          open
          close
        }
        wednesday {
          open
          close
        }
        thursday {
          open
          close
        }
        friday {
          open
          close
        }
        saturday {
          open
          close
        }
        sunday {
          open
          close
        }
      }
    }
  }
`;

export const itemFieldsFragment = gql`
  fragment itemFields on BagItem {
    productId
    productUrl
    productName
    itemType
    selectedSku {
      id
      originalPrice
      salePrice
      discountAmount
      originalPricePerItem
      salePricePerItem
      color {
        name
      }
      size
      image
      maxQuantity
      availableQuantity
    }
    itemId
    bvId
    quantity
    isHazmat
    isBopis
    isLowStock
    category {
      displayName
      unifiedId
    }
  }
`;

export const bagFieldsFragment = gql`
  ${summaryFieldsFragment}
  ${shippingAddressFieldsFragment}
  ${shippingMethodFieldsFragment}
  ${paymentGroupsFieldsFragment}
  ${itemFieldsFragment}
  ${bopisStoreFieldsFragment}
  ${giftMessageFieldsFragment}

  fragment bagFields on Bag {
    id
    orderNumber
    couponCode
    buyNowOrder
    encryptedOrderId
    guestEmailSignup
    hasWarningMessage
    warningInfos {
      type
      warningKey
      warningMessage
    }
    ...summaryFields
    shipping {
      ...shippingAddressFields
      countries {
        code
        displayName
        isSelected
      }
      ...shippingMethodFields
    }
    ...paymentGroupsFields
    items {
      overallDeliveryType
      totalItemCount
      shipItemCount
      bopisItemCount
      hasLoyaltyProduct
      shipItems {
        ...itemFields
        isHazmat
        isIneligible
        isFinalSale
      }
      bopisItems {
        ...itemFields
        isFinalSale
      }
    }
    ...bopisStoreFields
    ...giftMessageFields
    eddDisplayString
    estimatedDeliveryDate
    cvvRequired
  }
`;

export const userProfileFieldsFragment = gql`
  ${creditCardFieldsFragment}
  ${billingAddressFieldsFragment}

  fragment userProfileFields on UserProfile {
    email
    employeeType
    enableLululemonDiscount
    firstName
    id
    isLoggedIn
    isLoyaltyUser
    isLoyaltyGuest
    gender
    hashedEmail
    userType
    guestType
    allShippingAddresses {
      id
      firstName
      lastName
      address1
      address2
      city
      state
      postalCode
      country
      nickName
      phone
      isPrimary
      lululemonVerified
      encryptedAddressId
    }
    creditCards {
      ...creditCardFields
      ...billingAddressFields
    }
    loyaltyPrograms {
      country
      createdDate
      expiryDate
      id
      programCode
      programSubType
      type
      verificationStatus
      updatedDate
      verifiedDate
    }
    sortedLoyaltyPrograms {
      programsIdvx
      programsGih
    }
  }
`;

export const membershipFieldsFragment = gql`
  fragment membershipFields on Membership {
    id
    isBirthdayDiscountAvailable
    isBirthdayDiscountRedeemed
  }
`;

export const checkoutFieldsFragment = gql`
  ${bagFieldsFragment}
  ${userProfileFieldsFragment}

  fragment checkoutFields on Query {
    bag(checkForLowStock: true, bagId: $bagId) {
      ...bagFields
    }
    getBillingCountries {
      value: code
      label: displayName
    }
    userProfile(applyLocaleFilter: true, requestSource: "checkout") {
      ...userProfileFields
    }
  }
`;

//created checkoutFieldsWithBagIdFragment to avoid error in resetAnonymousSession and checkoutRefreshBag Query related to variable bagId
export const checkoutFieldsWithBagIdFragment = gql`
  ${bagFieldsFragment}
  ${userProfileFieldsFragment}

  fragment checkoutFieldsForBuyNow on Query {
    bag(bagId: $bagId) {
      ...bagFields
    }
    getBillingCountries {
      value: code
      label: displayName
    }
    userProfile(applyLocaleFilter: true, requestSource: "checkout") {
      ...userProfileFields
    }
  }
`;

export const savedGiftCardsFragment = gql`
  fragment savedGiftCards on Query {
    savedGiftCards(requestSource: "checkout", returnZeroBalance: false) {
      giftCardId
      amountAvailable
      lastFourDigits
    }
  }
`;

export const userProfileQuery = gql`
  ${userProfileFieldsFragment}

  query userProfileQuery {
    userProfile(applyLocaleFilter: true, requestSource: "checkout") {
      ...userProfileFields
    }
  }
`;

export const migrateToAnonymousBagQuery = gql`
  ${bagFieldsFragment}

  mutation migrateToAnonymousBag($bagId: String) {
    migrateToAnonymousBag(bagId: $bagId) {
      ...bagFields
    }
  }
`;

export const checkoutNoBagQuery = gql`
  ${userProfileFieldsFragment}

  query checkoutNoBag {
    getBillingCountries {
      value: code
      label: displayName
    }
    userProfile(applyLocaleFilter: true, requestSource: "checkout") {
      ...userProfileFields
    }
  }
`;

export const checkoutQuery = gql`
  ${checkoutFieldsWithBagIdFragment}

  query checkoutQuery($bagId: String) {
    ...checkoutFieldsForBuyNow
  }
`;

export const refreshCheckoutQuery = gql`
  ${checkoutFieldsFragment}
  ${savedGiftCardsFragment}

  query checkoutRefreshBag($bagId: String) {
    ...checkoutFields
    ...savedGiftCards
  }
`;

export const refreshBagQuery = gql`
  ${bagFieldsFragment}

  mutation refreshBag($bagId: String, $refreshEDDOnly: Boolean) {
    refreshBag(bagId: $bagId, refreshEDDOnly: $refreshEDDOnly) {
      ...bagFields
    }
  }
`;

export const removeFromBagMutation = gql`
  ${bagFieldsFragment}

  mutation checkoutRemoveFromBagQuery(
    $itemId: String!
    $sku: String
    $isBopis: Boolean
    $bagId: String
  ) {
    removeFromBag(
      itemId: $itemId
      sku: $sku
      isBopis: $isBopis
      bagId: $bagId
    ) {
      ...bagFields
    }
  }
`;

/**
 * GraphQL mutation for removing a "Buy Now" order.
 * This mutation removes a "Buy Now" order associated with a bag.
 *
 * @typedef {Object} RemoveBuyNowVariables
 * @property {string} bagId - The unique identifier for the bag associated with the "Buy Now" order.
 *
 * @typedef {Object} RemoveBuyNowResponse
 * @property {boolean} success - Indicates whether the "Buy Now" order was successfully removed.
 */
export const removeBuyNowMutation = gql`
  mutation removeBuyNowBagQuery($orderId: String!) {
    removeBuyNowBag(orderId: $orderId) {
      id
      items {
        totalItemCount
      }
      errorMessage
      errorCode
      warningInfos {
        type
        warningKey
        warningMessage
      }
    }
  }
`;

/**
 * GraphQL mutation for creating a new account.
 * This mutation creates a new user account with the provided details.
 *
 * @typedef {Object} CreateAccountVariables
 * @property {string} email - The email address for the new account.
 * @property {string} password - The password for the new account.
 * @property {string} firstName - The first name of the user.
 * @property {string} lastName - The last name of the user.
 * @property {boolean} subscribeToNewsletter - Indicates if the user wants to subscribe to the newsletter.
 *
 * @typedef {Object} CreateAccountResponse
 * @property {string} userId - The unique identifier for the newly created user.
 * @property {string} email - The email address of the newly created user.
 */
export const createAccountMutation = gql`
  mutation createProfile(
    $login: String!
    $password: String!
    $orderId: String
  ) {
    createProfile(login: $login, password: $password, orderId: $orderId) {
      isLoggedIn
    }
  }
`;

export const setShippingInfoMutation = gql`
  ${shippingAddressFieldsFragment}
  ${shippingMethodFieldsFragment}
  ${davAddressFieldsFragment}
  ${paymentGroupsFieldsFragment}
  ${summaryFieldsFragment}
  ${giftMessageFieldsFragment}
  ${bopisStoreFieldsFragment}
  ${itemFieldsFragment}

  mutation setShippingInfo(
    $addressId: String
    $address: AddressInput!
    $phoneNumber: String!
    $email: String!
    $guestEmailSignup: Boolean
    $saveAddress: Boolean
    $isBopisOnly: Boolean
    $suggestionSelected: Boolean
    $shipMethodName: String
    $estimatedDeliveryDate: String
    $isShallowCall: Boolean
    $externalId: String
    $bagId: String
  ) {
    setShippingInfo(
      addressId: $addressId
      address: $address
      phoneNumber: $phoneNumber
      email: $email
      guestEmailSignup: $guestEmailSignup
      saveAddress: $saveAddress
      isBopisOnly: $isBopisOnly
      suggestionSelected: $suggestionSelected
      shipMethodName: $shipMethodName
      estimatedDeliveryDate: $estimatedDeliveryDate
      isShallowCall: $isShallowCall
      externalId: $externalId
      bagId: $bagId
    ) {
      id
      errorMessage
      encryptedOrderId
      errorCode
      couponCode
      shipping {
        ...shippingAddressFields
        ...shippingMethodFields
      }
      davSuggestedAddress {
        ...davAddressFields
      }
      ...paymentGroupsFields
      ...summaryFields
      ...giftMessageFields
      ...bopisStoreFields
      items {
        overallDeliveryType
        totalItemCount
        shipItemCount
        bopisItemCount
        hasLoyaltyProduct
        shipItems {
          ...itemFields
          isHazmat
          isIneligible
          isFinalSale
        }
        bopisItems {
          ...itemFields
          isFinalSale
        }
      }
      eddDisplayString
      estimatedDeliveryDate
      cvvRequired
    }
  }
`;

export const updateSavedShippingAddressMutation = gql`
  ${davAddressFieldsFragment}

  mutation updateAddressQuery(
    $id: String!
    $address: AddressInput!
    $phone: String!
    $addrDavChecked: Boolean
    $suggestionSelected: Boolean
  ) {
    updateAddress(
      id: $id
      address: $address
      phone: $phone
      addrDavChecked: $addrDavChecked
      suggestionSelected: $suggestionSelected
    ) {
      id
      nickName
      firstName
      lastName
      phone
      address1
      address2
      city
      state
      postalCode
      country
      isPrimary
      lululemonVerified
      davSuggestedAddress {
        ...davAddressFields
      }
    }
  }
`;

export const addGiftMessageMutation = gql`
  mutation addGiftMessage(
    $bagId: String
    $to: NonEmptyString!
    $from: NonEmptyString!
    $message: NonEmptyString!
  ) {
    addGiftMessage(bagId: $bagId, to: $to, from: $from, message: $message)
  }
`;

export const removeGiftMessageMutation = gql`
  mutation removeGiftMessage($bagId: String) {
    removeGiftMessage(bagId: $bagId)
  }
`;

export const setBillingInfoMutation = gql`
  ${bagFieldsFragment}

  mutation setBillingInfo(
    $bagId: String
    $billingAddress: AddressInput!
    $creditCardInfo: CreditCardInput!
    $phoneNumber: String!
    $saveCreditCard: Boolean
    $tokenType: String
  ) {
    setBillingInfo(
      bagId: $bagId
      billingAddress: $billingAddress
      creditCardInfo: $creditCardInfo
      phoneNumber: $phoneNumber
      saveCreditCard: $saveCreditCard
      tokenType: $tokenType
    ) {
      ...bagFields
    }
  }
`;

export const updateAnonymousBillingInfoMutation = gql`
  ${bagFieldsFragment}

  mutation updateAnonymousBillingInfo(
    $billingAddress: AddressInput!
    $anonymousCardInfo: AnonymousCardInput!
    $phoneNumber: String!
    $bagId: String
  ) {
    updateAnonymousBillingInfo(
      billingAddress: $billingAddress
      anonymousCardInfo: $anonymousCardInfo
      phoneNumber: $phoneNumber
      bagId: $bagId
    ) {
      ...bagFields
    }
  }
`;

export const updateSavedPaymentInfoMutation = gql`
  ${creditCardFieldsFragment}
  ${billingAddressFieldsFragment}

  mutation updateSavedPaymentInfoQuery(
    $creditCardInfo: SavedCreditCardInput!
    $billingAddress: AddressInput!
    $phone: String!
  ) {
    updateSavedPaymentInfo(
      creditCardInfo: $creditCardInfo
      billingAddress: $billingAddress
      phone: $phone
    ) {
      ...creditCardFields
      ...billingAddressFields
    }
  }
`;

export const setSavedBillingInfoMutation = gql`
  ${bagFieldsFragment}

  mutation setSavedBillingInfo(
    $bagId: String
    $cardId: String!
    $cardVerificationNumber: String
  ) {
    setSavedBillingInfo(
      bagId: $bagId
      cardId: $cardId
      cardVerificationNumber: $cardVerificationNumber
    ) {
      ...bagFields
    }
  }
`;

export const applyCouponCodeMutation = gql`
  ${bagFieldsFragment}

  mutation applyCouponCodeQuery($bagId: String, $couponCode: String!) {
    applyCouponCode(bagId: $bagId, couponCode: $couponCode) {
      ...bagFields
    }
  }
`;

export const removeCouponCodeQuery = gql`
  ${bagFieldsFragment}

  query removeCouponCodeQuery($bagId: String) {
    removeCouponCode(bagId: $bagId) {
      ...bagFields
    }
  }
`;

/**
 * GraphQL query for retrieving SKU details.
 * This query fetches detailed information about a specific SKU (Stock Keeping Unit).
 *
 * @typedef {Object} GetSkuDetailsVariables
 * @property {string} skuId - The unique identifier for the SKU.
 *
 * @typedef {Object} GetSkuDetailsResponse
 * @property {string} id - The unique identifier for the SKU.
 * @property {string} name - The name of the product associated with the SKU.
 * @property {string} description - The description of the product.
 * @property {number} price - The price of the product.
 * @property {boolean} isAvailable - Indicates if the SKU is currently available.
 * @property {Array<Object>} images - The list of images associated with the SKU.
 * @property {string} images.url - The URL of the image.
 * @property {string} images.altText - The alternative text for the image.
 */
export const getSkuDetails = gql`
  query skuDetailsQuery($skuids: [String]!) {
    skuDetails(skuids: $skuids) {
      productId
      productName
      pdpURL
      skuId
      skuColor
      skuSize
      categoryId
      categoryName
      activities
      imageDetails {
        imageInfo
      }
    }
  }
`;

export const submitOrderMutation = gql`
  ${bagFieldsFragment}

  mutation submitOrder(
    $deviceFingerprintID: String
    $bagId: String
    $isEnhancedEddEnabled: Boolean
  ) {
    submitOrder(
      deviceFingerprintID: $deviceFingerprintID
      handleTaxErrors: true
      handleOutOfStockErrors: true
      bagId: $bagId
      isEnhancedEddEnabled: $isEnhancedEddEnabled
    ) {
      id
      encryptedOrderId
      errorCode
      errorMessage
      ...bagFields
      outOfStockItems {
        sku
        displayName
        color
        size
        quantity
        price
        imageURL
      }
    }
  }
`;

export const validateAddressMutation = gql`
  ${shippingAddressFieldsFragment}
  ${davAddressFieldsFragment}

  mutation validateAddress(
    $address: AddressInput!
    $phoneNumber: String!
    $email: String!
    $doDavCheck: Boolean
    $bagId: String
  ) {
    setShippingInfo(
      address: $address
      phoneNumber: $phoneNumber
      email: $email
      doDavCheck: $doDavCheck
      bagId: $bagId
    ) {
      errorMessage
      errorCode
      shipping {
        ...shippingAddressFields
      }
      davSuggestedAddress {
        ...davAddressFields
      }
    }
  }
`;

export const setAfterPayBillingInfoMutation = gql`
  ${bagFieldsFragment}

  mutation setAfterPayBillingInfo(
    $bagId: String
    $afterPayPaymentToken: String!
  ) {
    setAfterPayBillingInfo(
      bagId: $bagId
      afterPayPaymentToken: $afterPayPaymentToken
    ) {
      ...bagFields
    }
  }
`;

export const getOrderDetailsQuery = gql`
  ${bagFieldsFragment}

  query getOrderDetailsQuery($encryptedOrderId: String!) {
    getOrderDetails(encryptedOrderId: $encryptedOrderId) {
      ...bagFields
    }
  }
`;

/**
 * GraphQL query for retrieving available shipping methods.
 * This query fetches the list of shipping methods available for a given bag.
 *
 * @typedef {Object} GetShippingMethodsVariables
 * @property {string} bagId - The unique identifier for the bag.
 *
 * @typedef {Object} GetShippingMethodsResponse
 * @property {Array<Object>} shippingMethods - The list of available shipping methods.
 * @property {string} shippingMethods.code - The code for the shipping method.
 * @property {string} shippingMethods.displayName - The display name of the shipping method.
 * @property {number} shippingMethods.price - The price of the shipping method.
 * @property {string} shippingMethods.estimatedDeliveryText - The estimated delivery text for the shipping method.
 */
export const getShippingMethodsQuery = gql`
  query getShippingMethodsQuery(
    $countryCode: String!
    $skipShipUpgrade: Boolean
  ) {
    getShippingMethods(
      countryCode: $countryCode
      skipShipUpgrade: $skipShipUpgrade
    ) {
      code
      isSelected
      displayName
      ribbonTitle
      price
      estimatedDeliveryText
      orderBy {
        utc
      }
      getBy {
        pacific
        utc
        eddDisplayString
      }
    }
  }
`;

/**
 * GraphQL query for retrieving state or province information.
 * This query fetches the list of states or provinces for a given country.
 *
 * @typedef {Object} GetStateProvinceVariables
 * @property {string} countryCode - The ISO code of the country (e.g., "US", "CA").
 * @typedef {Object} GetStateProvinceResponse
 * @property {Array<Object>} statesOrProvinces - The list of states or provinces.
 * @property {string} statesOrProvinces.code - The code for the state or province.
 * @property {string} statesOrProvinces.name - The name of the state or province.
 */
export const getStateProvinceQuery = gql`
  query getStatesOrProvincesQuery($countryCode: String!) {
    getStatesOrProvinces(countryCode: $countryCode) {
      label: displayName
      value: code
    }
  }
`;

export const applyGiftCardMutation = gql`
  ${bagFieldsFragment}

  mutation applyGiftCard(
    $cardNumber: String!
    $pin: String!
    $save: Boolean
    $bagId: String
  ) {
    applyGiftCard(
      cardNumber: $cardNumber
      pin: $pin
      save: $save
      bagId: $bagId
    ) {
      ...bagFields
    }
  }
`;

export const applySavedGiftCardMutation = gql`
  ${bagFieldsFragment}

  mutation applySavedGiftCard($giftCardId: String!, $bagId: String) {
    applySavedGiftCard(giftCardId: $giftCardId, bagId: $bagId) {
      ...bagFields
    }
  }
`;

export const removeGiftCardMutation = gql`
  ${bagFieldsFragment}

  mutation removeGiftCard($id: String!, $bagId: String) {
    removePayment(id: $id, bagId: $bagId) {
      ...bagFields
    }
  }
`;

export const setKlarnaBillingInfoMutation = gql`
  ${bagFieldsFragment}

  mutation setKlarnaBillingInfo($bagId: String, $klarnaToken: String!) {
    setKlarnaBillingInfo(bagId: $bagId, klarnaToken: $klarnaToken) {
      ...bagFields
    }
  }
`;

/**
 * GraphQL query for retrieving a loyalty program discount code.
 * This query fetches the discount code associated with a loyalty program for a given user.
 * @typedef {Object} LoyaltyProgramDiscountCodeVariables
 * @property {string} userId - The unique identifier for the user.
 *
 * @typedef {Object} LoyaltyProgramDiscountCodeResponse
 * @property {string} discountCode - The discount code associated with the loyalty program.
 * @property {string} programName - The name of the loyalty program.
 */
export const loyaltyProgramDiscountCodeQuery = gql`
  query loyaltyProgramDiscountCode($program: String!, $countryCode: String!) {
    loyaltyProgramDiscountCode(program: $program, countryCode: $countryCode) {
      id
      type
      attributes {
        discountCode
        webDisplayLabel
        webTooltipLabel
      }
    }
  }
`;

export const removePaymentMutation = gql`
  ${bagFieldsFragment}

  mutation removePayment($id: String!, $bagId: String) {
    removePayment(id: $id, bagId: $bagId) {
      ...bagFields
    }
  }
`;

export const setLongPaypalBraintreeInfoMutation = gql`
  ${bagFieldsFragment}

  mutation setLongPaypalBrainTreeBillingInfo(
    $billingAddress: AddressInput!
    $email: String!
    $paymentNonce: String!
    $phoneNumber: String!
    $bagId: String
  ) {
    setLongPaypalBrainTreeBillingInfo(
      billingAddress: $billingAddress
      email: $email
      paymentNonce: $paymentNonce
      phoneNumber: $phoneNumber
      bagId: $bagId
    ) {
      ...bagFields
    }
  }
`;

/**
 * GraphQL query for resetting an anonymous session.
 * This query resets the session for an anonymous user, clearing any associated data.
 *
 * @typedef {Object} ResetAnonymousSessionResponse
 * @property {boolean} success - Indicates whether the anonymous session was successfully reset.
 
 */
export const resetAnonymousSessionQuery = gql`
  ${checkoutFieldsFragment}

  query resetAnonymousSession($bagId: String) {
    ...checkoutFields
  }
`;

/**
 * GraphQL query for retrieving saved gift cards.
 * This query fetches the list of saved gift cards associated with a user.
 *
 * @typedef {Object} SavedGiftCardsResponse
 * @property {Array<Object>} savedGiftCards - The list of saved gift cards.
 * @property {string} savedGiftCards.id - The unique identifier for the gift card.
 * @property {string} savedGiftCards.lastFour - The last four digits of the gift card number.
 * @property {number} savedGiftCards.balance - The remaining balance on the gift card.
 */
export const savedGiftCardsQuery = gql`
  query savedGiftCards($returnZeroBalance: Boolean) {
    savedGiftCards(
      requestSource: "checkout"
      returnZeroBalance: $returnZeroBalance
    ) {
      giftCardId
      amountAvailable
      lastFourDigits
    }
  }
`;

/**
 * GraphQL mutation for removing a bag.
 * This mutation deletes a bag and its associated data.
 *
 * @typedef {Object} RemoveBagVariables
 * @property {string} bagId - The unique identifier for the bag to be removed.
 *
 * @typedef {Object} RemoveBagResponse
 * @property {boolean} success - Indicates whether the bag was successfully removed.
 */
export const removeBagMutation = gql`
  mutation removeBag($cartId: String!) {
    removeBag(cartId: $cartId)
  }
`;

/**
 * GraphQL query for retrieving store information.
 * This query fetches details about a specific store.
 *
 * @typedef {Object} StoreInfoVariables
 * @property {string} storeId - The unique identifier for the store.
 *
 * @typedef {Object} StoreInfoResponse
 * @property {string} id - The unique identifier for the store.
 * @property {string} name - The name of the store.
 * @property {string} address - The address of the store.
 * @property {string} phone - The phone number of the store.
 
 */
export const storeInfoQuery = gql`
  query getStoreInfo($storeId: String!, $includeSpecialSchedule: Boolean) {
    getStoreInfo(
      storeId: $storeId
      includeSpecialSchedule: $includeSpecialSchedule
    ) {
      websiteUrl
      timezone
      specialSchedule {
        close
        date
        description
        open
      }
    }
  }
`;

/**
 * GraphQL query for retrieving the estimated delivery date.
 * This query fetches the estimated delivery date for items in a bag.
 *
 * @typedef {Object} GetEstimatedDeliveryDateVariables
 * @property {string} bagId - The unique identifier for the bag.
 *
 * @typedef {Object} GetEstimatedDeliveryDateResponse
 * @property {Array<Object>} estimatedDeliveryDates - The list of estimated delivery dates for items.
 * @property {string} estimatedDeliveryDates.itemId - The unique identifier for the item.
 * @property {string} estimatedDeliveryDates.date - The estimated delivery date for the item.
 */
export const getEstimatedDeliveryDateQuery = gql`
  query getEstimatedDeliveryDate(
    $zipCode: String!
    $bagId: String
    $requestedDeliveryDate: String
  ) {
    getEstimatedDeliveryDate(
      zipCode: $zipCode
      bagId: $bagId
      requestedDeliveryDate: $requestedDeliveryDate
    ) {
      shipMethod
      orderCutOffDate
      estimatedDeliveryDate
      eddDisplayString
    }
  }
`;

/**
 * GraphQL mutation for adding estimated delivery date to a bag.
 * This mutation adds an estimated delivery date to the specified bag.
 *
 * @typedef {Object} AddEstimatedDeliveryDateVariables
 * @property {string} bagId - The unique identifier for the bag.
 * @property {string} estimatedDeliveryDate - The estimated delivery date to add.
 *
 * @typedef {Object} AddEstimatedDeliveryDateResponse
 * @property {Object} addEstimatedDeliveryDate - The response from the mutation.
 * @property {string} addEstimatedDeliveryDate.estimatedDeliveryDate - The added estimated delivery date.
 */
export const addEstimatedDeliveryDateMutation = gql`
  mutation addEstimatedDeliveryDate(
    $bagId: String
    $estimatedDeliveryDate: UTCTimeStamp!
  ) {
    addEstimatedDeliveryDate(
      bagId: $bagId
      estimatedDeliveryDate: $estimatedDeliveryDate
    ) {
      estimatedDeliveryDate
    }
  }
`;

/**
 * GraphQL query for initiating a payment session.
 * This query retrieves the necessary details to start a payment session for a bag.
 *
 * @typedef {Object} PaymentSessionVariables
 * @property {string} bagId - The unique identifier for the bag.
 *
 * @typedef {Object} PaymentSessionResponse
 * @property {string} sessionId - The unique identifier for the payment session.
 * @property {string} paymentUrl - The URL to redirect the user for payment.
 */
export const paymentSessionQuery = gql`
  query getPaymentSessionQuery($input: PaymentSessionInput!) {
    getPaymentSession(input: $input) {
      sessionInfo {
        ... on CreditCardFormV2Session {
          captureContext
        }
      }
    }
  }
`;
